/**
 * PricebookBrowser (PRICEBOOK-PICK-01, Seth 2026-09-28: "I like that you can type
 * but being able to open a modal maybe with more details if you wanted to") — the
 * full pricebook window any picker can open: the same search as the type-ahead,
 * category / brand / in-stock filters, a roomy list, and the details of the
 * highlighted item (picture, part #, model, price, our cost, on hand, vendor
 * part #, warranty, notes). Add keeps the window open, so a furnace, a coil and a
 * thermostat go on in one trip; Done closes it.
 *
 * Competitor frame: HubSpot and Salesforce open an "add products" window with
 * search and filters; ServiceTitan and FieldEdge browse the book by category with
 * pictures; Jobber only types ahead. Pegasus: type ahead first, this window one
 * click away, the same search in both.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ExternalLink, Package, Plus, Search } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { scrollActiveIntoView } from "@/lib/keys";
import { useItemCategories, useManufacturers, type Item } from "./items";
import { itemCostOf, itemPrice, usePricebookSearch, type PricebookFilters } from "./pricebookSearch";

export const pbMoney = (n: number | null | undefined) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 });

/** Wait until typing pauses before searching. */
export function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function PricebookBrowser({
  open,
  onClose,
  onPick,
  initialQuery = "",
  pickLabel = "Add",
  multi = true,
  filters,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (it: Item) => void;
  /** What the window opens searching for (the type-ahead's text). */
  initialQuery?: string;
  /** The add button's words — "Add to Job", "Add to Invoice". */
  pickLabel?: string;
  /** Keep the window open after an add (default) — false closes it on the first pick. */
  multi?: boolean;
  /** A fixed scope from the caller (an equipment type, a brand). */
  filters?: PricebookFilters;
}) {
  const [q, setQ] = useState(initialQuery);
  const [cat, setCat] = useState("");
  const [brand, setBrand] = useState("");
  const [inStock, setInStock] = useState(false);
  const [sel, setSel] = useState(0);
  const [added, setAdded] = useState<string[]>([]);
  // Fresh every time it opens (the React "adjust state when a prop changes" pattern).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setQ(initialQuery); setCat(""); setBrand(""); setInStock(false); setSel(0); setAdded([]); }
  }

  const dq = useDebounced(q, 150);
  const { data: cats } = useItemCategories();
  const { data: brands } = useManufacturers();
  const { data, isFetching } = usePricebookSearch(dq, {
    ...filters,
    category: cat || filters?.category || null,
    manufacturer: brand || filters?.manufacturer || null,
    inStock: inStock || filters?.inStock,
    limit: 60,
    minChars: 1,
    browse: true,
    enabled: open,
  });
  const rows = data ?? [];
  const cur = rows[Math.min(sel, Math.max(rows.length - 1, 0))] ?? null;
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setSel(0); }, [dq, cat, brand, inStock]);
  useEffect(() => { scrollActiveIntoView(listRef.current, sel); }, [sel]);

  const pick = (it: Item) => {
    onPick(it);
    setAdded((a) => [...a, it.id]);
    if (!multi) onClose();
  };

  const selectCls = "h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pricebook"
      size="xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-slate-500" aria-live="polite">
            {added.length > 0 ? `${added.length} added` : rows.length >= 60 ? "Showing the best 60 — type more to narrow" : ""}
          </span>
          <Button variant="secondary" size="sm" onClick={onClose}>Done</Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-autofocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, Math.max(rows.length - 1, 0))); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
                else if (e.key === "Enter" && cur) { e.preventDefault(); pick(cur); }
              }}
              placeholder="Part #, model, size, brand…"
              aria-label="Search the pricebook"
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
            />
          </label>
          <select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category" className={selectCls}>
            <option value="">All categories</option>
            {(cats ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {!filters?.manufacturer && (
            <select value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand" className={cn(selectCls, "max-w-[180px]")}>
              <option value="">All brands</option>
              {(brands ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
          <button
            type="button"
            aria-pressed={inStock}
            onClick={() => setInStock((v) => !v)}
            className={cn(
              "h-10 rounded-full px-4 text-sm font-semibold transition-colors",
              inStock ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50",
            )}
          >
            In Stock
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_300px]">
          <div ref={listRef} role="listbox" aria-label="Pricebook items" className="h-[56vh] min-h-[320px] overflow-y-auto rounded-xl border border-hairline">
            {rows.length === 0 ? (
              <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
                {isFetching ? "Searching…" : dq.trim() ? `Nothing in the pricebook matches “${dq.trim()}”.` : "Type to search, or pick a category."}
              </div>
            ) : (
              rows.map((it, i) => {
                const on = i === sel;
                const isAdded = added.includes(it.id);
                const price = itemPrice(it);
                const onHand = it.qty_in_stock != null && Number(it.qty_in_stock) > 0 ? Number(it.qty_in_stock) : null;
                return (
                  <div
                    key={it.id}
                    role="option"
                    aria-selected={on}
                    data-index={i}
                    onClick={() => setSel(i)}
                    onDoubleClick={() => pick(it)}
                    className={cn(
                      "group flex cursor-pointer items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0",
                      on ? "bg-brand-50" : "hover:bg-slate-50",
                    )}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-slate-200">
                      {it.image_url ? <img src={it.image_url} alt="" className="max-h-full max-w-full object-contain" loading="lazy" /> : <Package className="h-4 w-4 text-slate-300" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-slate-800">{it.description || it.part_number}</span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        <span className="font-mono text-slate-600">{it.part_number}</span>
                        {[it.manufacturer, it.category].filter(Boolean).map((x) => ` · ${x}`).join("")}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums text-slate-800">{pbMoney(price)}</span>
                      {onHand != null && <span className="block text-xs font-medium text-emerald-700">{onHand} on hand</span>}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); pick(it); }}
                      aria-label={`${pickLabel}: ${it.description ?? it.part_number}`}
                      title={pickLabel}
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
                        isAdded ? "bg-emerald-600 text-white" : "bg-white text-brand-700 ring-1 ring-inset ring-brand-200 hover:bg-brand-600 hover:text-white",
                      )}
                    >
                      {isAdded ? <Check className="h-4 w-4" strokeWidth={3} /> : <Plus className="h-4 w-4" />}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <aside className="flex h-[56vh] min-h-[320px] flex-col overflow-y-auto rounded-xl border border-hairline bg-slate-50/60 p-4" aria-label="Item details">
            {cur ? (
              <>
                <div className="mb-3 flex h-36 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-slate-200">
                  {cur.image_url ? <img src={cur.image_url} alt="" className="max-h-full max-w-full object-contain" /> : <Package className="h-10 w-10 text-slate-300" />}
                </div>
                <div className="text-base font-bold leading-snug text-slate-900">{cur.description || cur.part_number}</div>
                <div className="mt-1 font-mono text-sm text-slate-600">{cur.part_number}</div>
                <dl className="mt-3 space-y-1.5 text-sm">
                  <Fact k="Price" strong>{pbMoney(itemPrice(cur))}</Fact>
                  <Fact k="Our cost">{pbMoney(itemCostOf(cur))}</Fact>
                  <Fact k="On hand">{cur.qty_in_stock != null ? String(Number(cur.qty_in_stock)) : "—"}</Fact>
                  {cur.model && <Fact k="Model" mono>{cur.model}</Fact>}
                  {cur.manufacturer && <Fact k="Brand">{cur.manufacturer}</Fact>}
                  {cur.category && <Fact k="Category">{[cur.category, cur.subcategory && cur.subcategory !== cur.category ? cur.subcategory : null].filter(Boolean).join(" › ")}</Fact>}
                  {cur.efficiency && <Fact k="Efficiency">{cur.efficiency}</Fact>}
                  {cur.units && <Fact k="Sold by">{cur.units}</Fact>}
                  {cur.vendor_part_number && <Fact k="Vendor part #" mono>{cur.vendor_part_number}</Fact>}
                  {cur.warranty_months ? <Fact k="Warranty">{`${cur.warranty_months} months`}</Fact> : null}
                </dl>
                {cur.notes && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-600">{cur.notes}</p>}
                {cur.website_url && (
                  <a href={cur.website_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
                    Product page <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                <div className="mt-auto pt-4">
                  <Button className="w-full" onClick={() => pick(cur)}>
                    {added.includes(cur.id) ? <><Check className="h-4 w-4" strokeWidth={3} /> Added — Add Another</> : <><Plus className="h-4 w-4" /> {pickLabel}</>}
                  </Button>
                </div>
              </>
            ) : (
              <p className="m-auto text-center text-sm text-slate-400">Pick an item to see its details.</p>
            )}
          </aside>
        </div>
      </div>
    </Modal>
  );
}

function Fact({ k, children, strong, mono }: { k: string; children: ReactNode; strong?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-slate-500">{k}</dt>
      <dd className={cn("min-w-0 truncate text-right text-slate-800", strong && "font-semibold", mono && "font-mono")}>{children}</dd>
    </div>
  );
}
